(() => {
  "use strict";
  /* Immediate Debut preview applet. Boots from window.SWISH_DEBUT_PREVIEW
     (set by snippets/swish-debut-preview.liquid) and mounts into the format
     studio's Debut panel through the swish:debut-preview event the studio
     dispatches when it renders a Debut detail card. Shopper safety rules:
     the applet only ever talks to the preview worker's fixed routes, renders
     the eligible design the worker's catalog exposes for this product, and
     an ineligible colour shows the plain garment without artwork. Selection
     updates are token-fenced: a stale response can never overwrite the
     current choice, and while a render is loading or failing the last good
     preview stays on screen. */
  const CONFIG = window.SWISH_DEBUT_PREVIEW;
  if (!CONFIG || !CONFIG.origin) return;
  if (!/^[1-9][0-9]{0,15}$/.test(String(CONFIG.productId))) return;
  /* The Worker knows a design by its design product. On a mug or coaster
     page the format engine resolves the base design product (the same
     stripped handle it uses for sibling formats) into designProductId, so
     garment cards look up that design's art; every other page looks up its
     own product. */
  const lookupId = () => /^[1-9][0-9]{0,15}$/.test(String(CONFIG.designProductId)) ? String(CONFIG.designProductId) : String(CONFIG.productId);
  const productPrefix = () => "/debut/preview/v1/product/" + lookupId();
  // Finished photographs are bound to the actual product and exact ordered
  // selection. Parent designProductId is intentionally used only above for raw art.
  const finishedCatalogs = new Map();
  const finishedBindings = new WeakMap();
  const decimal = (v) => typeof v === "string" && /^[1-9][0-9]*$/.test(v);
  function finishedCatalog(productId) {
    if (!finishedCatalogs.has(productId)) {
      const load = fetch(CONFIG.origin + "/debut/preview/v1/finished/product/" + productId + "/catalog", {mode: "cors"})
        .then(async (r) => { if (!r.ok) { await r.arrayBuffer(); throw Error("finished-catalog"); } return r.json(); })
        .catch(() => { finishedCatalogs.delete(productId); return null; });
      finishedCatalogs.set(productId, load);
      if (finishedCatalogs.size > 24) finishedCatalogs.delete(finishedCatalogs.keys().next().value);
    }
    return finishedCatalogs.get(productId);
  }
  document.addEventListener("swish:finished-card", (event) => {
    const {image, selection} = event.detail || {};
    if (!(image instanceof HTMLImageElement)) return;
    finishedBindings.get(image)?.();
    if (!selection || !decimal(selection.productId) || !decimal(selection.variantId) || !Array.isArray(selection.selectedOptions)) return;
    const photo = {src: image.getAttribute("src"), srcset: image.getAttribute("srcset"), alt: image.alt, crossOrigin: image.getAttribute("crossorigin")};
    const dialog = image.closest("dialog");
    let alive = true, probe = null;
    const current = () => alive && image.isConnected && dialog?.open !== false;
    const cleanup = () => {
      if (!alive) return;
      alive = false;
      observer.disconnect();
      dialog?.removeEventListener("close", cleanup);
      image.removeEventListener("error", finalError);
      if (probe) { probe.onload = null; probe.onerror = null; probe.removeAttribute("src"); }
      if (image.dataset.swpFinished) {
        if (photo.crossOrigin === null) image.removeAttribute("crossorigin"); else image.setAttribute("crossorigin", photo.crossOrigin);
        if (photo.src === null) image.removeAttribute("src"); else image.src = photo.src;
        if (photo.srcset === null) image.removeAttribute("srcset"); else image.setAttribute("srcset", photo.srcset);
        image.alt = photo.alt;
        delete image.dataset.swpFinished;
      }
      finishedBindings.delete(image);
    };
    const finalError = () => { if (current()) cleanup(); };
    const observer = new MutationObserver(() => { if (!current()) cleanup(); });
    observer.observe(document.body, {childList: true, subtree: true, attributes: true, attributeFilter: ["open"]});
    dialog?.addEventListener("close", cleanup);
    finishedBindings.set(image, cleanup);
    finishedCatalog(selection.productId).then(async (catalogue) => {
      if (!current() || !catalogue || !Array.isArray(catalogue.rows)) return;
      const row = catalogue.rows.find((r) => r.sourceKind === "shopify-finished-product-card" && r.productId === selection.productId && r.variantId === selection.variantId && r.formatFamily === selection.formatFamily && Array.isArray(r.selectedOptions) && r.selectedOptions.length === selection.selectedOptions.length && r.selectedOptions.every((o, i) => o.name === selection.selectedOptions[i].name && o.value === selection.selectedOptions[i].value));
      if (!row || !["image/jpeg", "image/png"].includes(row.mime) || !Number.isInteger(row.width) || !Number.isInteger(row.height) || row.width < 1 || row.height < 1 || row.width > 4096 || row.height > 4096 || row.width * row.height > 16777216 || typeof row.imagePath !== "string" || !new RegExp("^/debut/preview/v1/finished/product/" + selection.productId + "/variant/" + selection.variantId + "/[a-f0-9]{64}$").test(row.imagePath)) return;
      const ready = new Promise((resolve, reject) => {
        probe = new Image();
        probe.crossOrigin = "anonymous";
        probe.onload = resolve;
        probe.onerror = reject;
        probe.src = CONFIG.origin + row.imagePath;
      });
      await ready;
      await probe.decode();
      if (!current() || probe.naturalWidth !== row.width || probe.naturalHeight !== row.height) return;
      image.addEventListener("error", finalError);
      image.removeAttribute("srcset");
      image.crossOrigin = "anonymous";
      image.src = probe.src;
      image.dataset.swpFinished = "true";
      image.alt = photo.alt + " · Finished product photo";
    }).catch(() => {});
  });
  const L = CONFIG.labels || {};
  const text = (key, fallback) => (L[key] || fallback);
  const catalogPromise = {current: null, id: null};
  const choices = new Map();
  const mounts = new WeakMap();
  function catalog() {
    if (!catalogPromise.current || catalogPromise.id !== lookupId()) {
      const prefix = productPrefix();
      catalogPromise.id = lookupId();
      const load = catalogPromise.current = fetch(CONFIG.origin + prefix + "/catalog?artwork=choose-v1", {mode: "cors"})
        .then((r) => r.status === 400 ? fetch(CONFIG.origin + prefix + "/catalog", {mode: "cors"}) : r)
        .then((r) => { if (r.status === 404) return {designs: []}; if (!r.ok) throw new Error("catalog"); return r.json(); })
        .catch((e) => { if (catalogPromise.current === load) catalogPromise.current = null; throw e; });
    }
    return catalogPromise.current;
  }
  /* Format-chooser intent (pointer, focus or activation of the studio's
     Compare button or its Existing/Be the first modes) starts this product's
     one catalogue read before the Debut cards ask for it. It is the same
     memoized read, so cards, panel and reopen never repeat it; at most one
     warm attempt per page, and a failure only clears the memo so the cards'
     own read retries truthfully. */
  let catalogWarmed = false;
  const warmCatalog = (event) => {
    if (catalogWarmed || !(event.target instanceof Element) || !event.target.closest(".sw-studio__open, .sw-format__modes button")) return;
    catalogWarmed = true;
    catalog().catch(() => {});
  };
  for (const type of ["pointerover", "pointerdown", "focusin", "click"])
    document.addEventListener(type, warmCatalog, {capture: true, passive: true});
  const bitmaps = new Map();
  function bitmap(path) {
    if (!bitmaps.has(path)) {
      const load = fetch(CONFIG.origin + path, {mode: "cors"})
        .then((r) => { if (!r.ok) throw new Error("asset"); return r.blob(); })
        .then((blob) => createImageBitmap(blob));
      bitmaps.set(path, load);
      load.catch(() => bitmaps.delete(path));
    }
    return bitmaps.get(path);
  }
  const geometries = new Map();
  function geometry(designId, colourId) {
    const key = lookupId() + "/" + designId + "/" + colourId;
    if (!geometries.has(key)) {
      const load = fetch(CONFIG.origin + productPrefix() + "/geometry/" + designId + "/" + colourId + "?width=600", {mode: "cors"})
        .then((r) => { if (!r.ok) throw new Error("geometry"); return r.json(); });
      geometries.set(key, load);
      load.catch(() => geometries.delete(key));
    }
    return geometries.get(key);
  }
  let styled = false;
  function ensureStyle() {
    if (styled) return;
    styled = true;
    const style = document.createElement("style");
    style.textContent = ".swp{border:1px solid #e7dfec;background:#fbf8fc;border-radius:9px;padding:13px;margin:16px 0 4px}"
      + ".swp__eyebrow{margin:0 0 9px;font-size:11px;font-weight:800;letter-spacing:.05em;color:#702c88}"
      + ".swp__frame{position:relative;background:#fff;border-radius:7px;overflow:hidden}"
      + ".swp__frame img,.swp__frame canvas{display:block;width:100%;height:auto;aspect-ratio:1;object-fit:contain}"
      + ".swp__frame canvas{background:#fff}"
      + ".swp__final{position:absolute;inset:0}"
      + ".swp__final[hidden]{display:none}"
      + ".swp__row{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-top:10px;flex-wrap:wrap}"
      + ".swp__swatches{display:flex;flex-wrap:wrap;gap:7px;min-width:0;max-width:100%}"
      + ".swp__swatch{position:relative;width:34px;height:34px;padding:3px;border-radius:50%;border:1px solid #cfc1d6;background:#fff;cursor:pointer;flex:0 0 34px}"
      + ".swp__swatch[aria-pressed=true]{outline:2px solid #702c88;outline-offset:2px;border-color:#702c88}"
      + ".swp__chip{display:block;width:100%;height:100%;border-radius:50%;border:1px solid #0002}"
      + ".swp__status{font-size:12px;color:#6b5b73;margin:0;min-width:0;text-align:right}"
      + ".swp__status:empty{display:none}"
      + ".swp__artworks{display:flex;flex-wrap:wrap;gap:8px;margin:9px 0;max-width:100%;max-height:184px;overflow-y:auto;padding:4px}"
      + ".swp__artwork{width:76px;padding:4px;border:1px solid #cfc1d6;border-radius:7px;background:#fff;color:#6b5b73;cursor:pointer;font-size:11px}"
      + ".swp__artwork canvas{display:block;width:66px;height:66px;object-fit:contain}"
      + ".swp__artwork[aria-pressed=true],.swp__artwork:focus-visible{outline:2px solid #702c88;outline-offset:2px}"
      + ".swp__note{font-size:11px;color:#75627e;margin:9px 0 0}"
      // Pending-card marker: absolutely positioned over the card's image area
      // so it never shifts the layout, and click-through so it never blocks
      // the card's own selection button.
      + ".sw-format__card{position:relative}"
      + ".swp__card-pending{position:absolute;top:8px;left:8px;z-index:1;max-width:calc(100% - 16px);padding:2px 7px;border:1px solid #e7dfec;border-radius:7px;background:#fbf8fc;color:#702c88;font-size:11px;font-weight:800;line-height:1.25;text-align:left;pointer-events:none}";
    document.head.appendChild(style);
  }
  const el = (tag, cls, textContent) => {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (textContent != null) node.textContent = textContent;
    return node;
  };
  const debug = {mounts: 0, last: null};
  window.SwishDebutPreview = {
    debug: () => ({...debug.last, mounts: debug.mounts}),
  };
  const bitmapPixels = new WeakMap();
  function pixelsOf(bitmap) {
    if (!bitmapPixels.has(bitmap)) {
      const canvas = document.createElement("canvas");
      canvas.width = bitmap.width; canvas.height = bitmap.height;
      const ctx = canvas.getContext("2d", {willReadFrequently: true});
      ctx.drawImage(bitmap, 0, 0);
      bitmapPixels.set(bitmap, ctx.getImageData(0, 0, bitmap.width, bitmap.height).data);
    }
    return bitmapPixels.get(bitmap);
  }
  function paint(ctx, base, colourHex, art, placement, background = null) {
    ctx.fillStyle = colourHex;
    ctx.fillRect(0, 0, 600, 600);
    ctx.imageSmoothingEnabled = false;
    if (background && base) {
      // Match the finite Worker's floor sampling and straight-alpha layer order.
      // Native drawImage samples different texels when 1000px fabric becomes 600px.
      const out = ctx.createImageData(600, 600), bp = pixelsOf(base), tp = pixelsOf(background);
      for (let y = 0; y < 600; y++) for (let x = 0; x < 600; x++) {
        const i = (y * 600 + x) * 4;
        const b = (Math.floor(y * base.height / 600) * base.width + Math.floor(x * base.width / 600)) * 4;
        const t = (Math.floor(y * background.height / 600) * background.width + Math.floor(x * background.width / 600)) * 4;
        const sa = bp[b + 3] / 255, da = tp[t + 3] / 255, a = sa + da * (1 - sa);
        for (let k = 0; k < 3; k++) out.data[i + k] = a ? Math.round((bp[b + k] * sa + tp[t + k] * da * (1 - sa)) / a) : 0;
        out.data[i + 3] = Math.round(a * 255);
      }
      ctx.putImageData(out, 0, 0);
    } else if (base) ctx.drawImage(base, 0, 0, 600, 600);
    // The worker crops the artwork to its visible bounds and fits that crop
    // into the destination; drawing the same source rect keeps the immediate
    // preview and the finished image on one canonical placement.
    if (art && placement) {
      const d = placement.destination, b = placement.visibleBounds;
      ctx.drawImage(art, b.x, b.y, b.width, b.height, d.left, d.top, d.width, d.height);
    }
  }
  const previewChoiceKey = (formatKey) => lookupId() + "/" + formatKey;
  // The format engine's Basic Tee and Daily Dozen share producer catalog 12
  // (style 707, dtg/front); keep the preview catalog's canonical family key.
  const garmentFor = (data, formatKey) => {
    const previewKey = formatKey === "basic-tee" ? "gildan-softstyle-tee" : formatKey;
    return (data.garments || [data.garment]).find((g) => g && g.debutKey === previewKey) || null;
  };
  const luma = (hex) => {
    const v = [1, 3, 5].map((n) => parseInt(hex.slice(n, n + 2), 16));
    return (2126 * v[0] + 7152 * v[1] + 722 * v[2]) / 10000;
  };

  /* Format-card thumbnails. The studio dispatches swish:debut-cards with the
     Debut cards it just rendered ({image, formatKey, label}). A card shows
     this product's artwork composed on its garment by the Worker's accepted
     compositor, at card resolution (render ?width=300), only when the
     artwork is unambiguous: the single automatic design, the pair member the
     catalogue exposes on the card's colour, or the shopper's explicit choice
     for that format in the preview panel. Otherwise the card keeps its photo.
     The card's colour follows the panel's last colour for that format when
     the artwork is offered on it, else the lightest offered colour (the
     panel's initial colour). Requests start only for visible cards, in card
     order, at most two at a time; queued work is dropped and in-flight work
     aborted when the cards are re-rendered or the dialog closes. A response
     applies only if its card still wants that exact identity; a failure
     leaves the photo and its alt in place. A card with scheduled work
     (queued, fetching or decoding) carries a compact visible pending marker
     until a thumbnail applies or the work fails, is superseded or released,
     so its temporary photo is never mistaken for the finished preview. */
  const CARD_WIDTH = 300;
  const CARD_CONCURRENCY = 2;
  const CARD_DEADLINE = 8000;
  const CARD_BODY_LIMIT = 1000000;
  const CARD_CACHE = {entries: 24, bytes: 6000000};
  const cardBlobs = new Map();
  const cardFlights = new Map();
  const cardColours = new Map();
  const decodedBlobs = new WeakSet();
  let cardScope = null;
  let cardRoute = true;
  const cardDebug = {requests: 0, applied: 0, staleDropped: 0, failed: 0, aborted: 0, urls: 0};
  // Every card object URL is counted while owned, so disposal is observable.
  const cardUrl = (blob) => { cardDebug.urls += 1; return URL.createObjectURL(blob); };
  const dropCardUrl = (url) => { cardDebug.urls -= 1; URL.revokeObjectURL(url); };
  window.SwishDebutPreview.cards = () => ({
    ...cardDebug,
    cached: cardBlobs.size,
    cachedBytes: Array.from(cardBlobs.values()).reduce((n, b) => n + b.size, 0),
    inFlight: cardFlights.size,
    queued: cardScope ? cardScope.queue.length : 0,
    cards: cardScope ? cardScope.bindings.map((b) => ({formatKey: b.formatKey, state: b.image.dataset.swpCard || "photo", identity: b.shown || null})) : [],
  });
  const REVIEWED_REPRESENTATIVES = {"1922116417/741698":{"basic-tee":"474","tank-top":"13591","triblend-t-shirt":"6584"},"1967179457/783815":{"basic-tee":"474","tank-top":"13591","triblend-t-shirt":"6584"},"2059995457/772649":{"basic-tee":"474","tank-top":"13591","triblend-t-shirt":"6584"}};
  function cardSpec(data, formatKey) {
    const designs = (data.designs || []).filter((d) => (d.productIds || []).includes(lookupId()));
    const garment = garmentFor(data, formatKey);
    if (!designs.length || !garment) return null;
    const explicit = data.artworkSelection === "explicit";
    const artworkId = explicit ? choices.get(previewChoiceKey(formatKey)) || null : null;
    if (explicit && !designs.some((d) => d.id === artworkId)) return null;
    const designFor = (colour) =>
      designs.find((d) => (!explicit || d.id === artworkId) && !!colour.eligible && (d.eligibleVariants || []).includes(String(colour.id))) || null;
    const colours = (garment.colours || []).filter(designFor);
    const chosen = colours.find((c) => String(c.id) === cardColours.get(formatKey));
    const reviewed = !explicit && designs.find((d) => REVIEWED_REPRESENTATIVES[lookupId() + '/' + d.id]?.[formatKey]);
    const preferred = reviewed && colours.find((c) => String(c.id) === REVIEWED_REPRESENTATIVES[lookupId() + '/' + reviewed.id][formatKey] && designFor(c)?.id === reviewed.id);
    const colour = chosen || preferred || colours.slice().sort((a, b) => luma(b.hex) - luma(a.hex))[0];
    if (!colour) return null;
    const design = designFor(colour);
    const artwork = explicit
      ? text("artwork", "Artwork") + " " + (designs.indexOf(design) + 1)
      : text("card_artwork", "this design");
    const cardPath = data.cardPaths?.[design.id + "/" + colour.id];
    return {
      path: cardPath || productPrefix() + "/render/" + design.id + "/" + colour.id + "?width=" + CARD_WIDTH,
      identity: [cardPath ? data.version + ":" + cardPath.split("&v=")[1] : data.version, data.generation, lookupId(), formatKey, design.id, colour.id, CARD_WIDTH].join("/"),
      artwork,
      colour: colour.name,
    };
  }
  // Abandon the identity immediately, but retain the logical queue slot until
  // fetch/read actually settles. Abort is a request, not proof of settlement.
  function stopCardFlight(flight) {
    if (flight.controller.signal.aborted || flight.settled) return;
    clearTimeout(flight.timer);
    if (cardFlights.get(flight.identity) === flight) cardFlights.delete(flight.identity);
    cardDebug.aborted += 1;
    flight.controller.abort();
    for (const binding of flight.users) {
      if (binding.flight === flight && binding.spec?.identity === flight.identity)
        setCardState(binding, binding.url ? "composed" : "photo");
    }
  }
  async function readCardBody(response, flight) {
    // Native supported browsers expose a reader. Without one, fail closed:
    // Content-Length cannot bound an opaque, whole-body blob() allocation.
    const reader = response.body?.getReader();
    if (!reader) throw new Error("card");
    let complete = false;
    try {
      if (flight.controller.signal.aborted) throw new Error("card");
      if (response.status === 400) cardRoute = false;
      if (!response.ok || response.headers.get("content-type")?.toLowerCase() !== "image/png") throw new Error("card");
      const chunks = [];
      let size = 0;
      while (true) {
        const {done, value} = await reader.read();
        if (flight.controller.signal.aborted) throw new Error("card");
        if (done) { complete = true; break; }
        if (value.byteLength > CARD_BODY_LIMIT - size) throw new Error("card");
        size += value.byteLength;
        chunks.push(value);
      }
      if (!size) throw new Error("card");
      return new Blob(chunks, {type: "image/png"});
    } finally {
      try { if (!complete) await reader.cancel(); }
      catch { /* Cancellation rejection still releases the reader lock. */ }
      finally { reader.releaseLock(); }
    }
  }
  function cardBlob(binding) {
    const {identity, path} = binding.spec;
    const cached = cardBlobs.get(identity);
    if (cached) {
      cardBlobs.delete(identity);
      cardBlobs.set(identity, cached);
      return Promise.resolve(cached);
    }
    let flight = cardFlights.get(identity);
    if (!flight) {
      const controller = new AbortController();
      cardDebug.requests += 1;
      flight = {identity, controller, users: new Set(), promise: null, timer: null, settled: false};
      flight.timer = setTimeout(() => stopCardFlight(flight), CARD_DEADLINE);
      flight.promise = Promise.resolve().then(() => fetch(CONFIG.origin + path, {mode: "cors", signal: controller.signal}))
        .then((r) => readCardBody(r, flight))
        .then((blob) => {
          if (controller.signal.aborted || !flight.users.size || cardFlights.get(identity) !== flight) throw new Error("card");
          cardBlobs.set(identity, blob);
          let total = Array.from(cardBlobs.values()).reduce((n, b) => n + b.size, 0);
          while (cardBlobs.size > CARD_CACHE.entries || total > CARD_CACHE.bytes) {
            const [oldest, old] = cardBlobs.entries().next().value;
            cardBlobs.delete(oldest);
            total -= old.size;
          }
          return blob;
        })
        .finally(() => {
          flight.settled = true;
          clearTimeout(flight.timer);
          if (cardFlights.get(identity) === flight) cardFlights.delete(identity);
        });
      cardFlights.set(identity, flight);
    }
    flight.users.add(binding);
    binding.flight = flight;
    return flight.promise;
  }
  function releaseFlight(binding) {
    const flight = binding.flight;
    binding.flight = null;
    if (!flight) return;
    flight.users.delete(binding);
    if (!flight.users.size) stopCardFlight(flight);
  }
  const cardLive = (scope, binding) =>
    cardScope === scope && binding.image.isConnected && binding.image.closest("dialog")?.open !== false;
  function setCardState(binding, value) {
    binding.image.dataset.swpCard = value;
    if (binding.image.dataset.swpEmpty) binding.image.style.visibility = value === "composed" || binding.url ? "" : "hidden";
    if (value === "loading" || value === "queued") binding.card?.setAttribute("aria-busy", "true");
    else binding.card?.removeAttribute("aria-busy");
    setCardMarker(binding, value === "loading" || value === "queued");
    const scope = cardScope;
    if (scope?.firstPaintDone && !scope.bindings.slice(0, 3).some((b) => b.visible && /^(queued|loading)$/.test(b.image.dataset.swpCard))) {
      scope.firstPaintDone();
      scope.firstPaintDone = null;
    }
  }
  // The visible pending marker rides on the same state change as aria-busy,
  // so every path out of scheduled work (applied, failed, superseded,
  // released) clears it through setCardState.
  function setCardMarker(binding, pending) {
    if (pending) {
      if (binding.marker?.isConnected) return;
      binding.marker = el("span", "swp__card-pending", text("card_generating", "Generating preview…"));
      binding.card?.append(binding.marker);
    } else if (binding.marker) {
      binding.marker.remove();
      binding.marker = null;
    }
  }
  function showPhoto(binding) {
    if (binding.url) {
      binding.image.src = binding.photo.src;
      if (binding.photo.srcset) binding.image.srcset = binding.photo.srcset;
      binding.image.alt = binding.photo.alt;
      dropCardUrl(binding.url);
      binding.url = null;
    }
    binding.shown = null;
    setCardState(binding, "photo");
  }
  function applyCard(scope, binding, spec, blob, decoded = false) {
    const url = cardUrl(blob);
    const swap = () => {
      if (!cardLive(scope, binding) || binding.spec !== spec) {
        dropCardUrl(url);
        cardDebug.staleDropped += 1;
        return;
      }
      if (binding.url) dropCardUrl(binding.url);
      binding.url = url;
      binding.shown = spec.identity;
      binding.image.removeAttribute("srcset");
      binding.image.src = url;
      decodedBlobs.add(blob);
      binding.image.alt = text("card_alt", "{format} preview with {artwork} on {colour}")
        .replace("{format}", binding.label).replace("{artwork}", spec.artwork).replace("{colour}", spec.colour);
      setCardState(binding, "composed");
      cardDebug.applied += 1;
    };
    // A cached thumbnail already decoded once: swap in the same task so a
    // re-rendered card never flashes its photo. A new response decodes
    // off-DOM first, so a stale or broken response never reaches the card.
    if (decoded) { swap(); return; }
    // A cached blob whose first consumer went stale still needs decoding.
    setCardState(binding, "loading");
    const probe = new Image();
    probe.onload = swap;
    probe.onerror = () => {
      dropCardUrl(url);
      if (!cardLive(scope, binding) || binding.spec !== spec) return;
      cardDebug.failed += 1;
      // A composed card keeps its truthful last image and leaves the pending
      // state; a photo card falls back to its photo. Either way the card is
      // no longer loading.
      setCardState(binding, binding.url ? "composed" : "photo");
    };
    probe.src = url;
  }
  function pumpCards(scope) {
    if (!cardRoute) {
      // The card route is off for this session, so nothing queued will ever
      // run: no card may keep a pending indication.
      while (scope.queue.length) {
        const binding = scope.queue.shift();
        setCardState(binding, binding.url ? "composed" : "photo");
      }
    }
    while (cardScope === scope && cardRoute && scope.running < CARD_CONCURRENCY && scope.queue.length) {
      const binding = scope.queue.shift();
      const spec = binding.spec;
      if (!spec || binding.shown === spec.identity || !cardLive(scope, binding)) continue;
      scope.running += 1;
      setCardState(binding, "loading");
      const work = cardBlob(binding);
      const flight = binding.flight;
      work
        .then((blob) => {
          if (!cardLive(scope, binding) || binding.spec !== spec) { cardDebug.staleDropped += 1; return; }
          applyCard(scope, binding, spec, blob);
        })
        .catch(() => {
          if (!cardLive(scope, binding) || binding.spec !== spec) return;
          cardDebug.failed += 1;
          if (binding.url) setCardState(binding, "composed"); else setCardState(binding, "photo");
        })
        .finally(() => {
          if (binding.flight === flight) binding.flight = null;
          flight?.users.delete(binding);
          scope.running -= 1;
          pumpCards(scope);
        });
    }
  }
  function queueCard(scope, binding) {
    const spec = binding.spec;
    if (!spec) { showPhoto(binding); return; }
    if (binding.shown === spec.identity) return;
    const cached = cardBlobs.get(spec.identity);
    if (cached) { applyCard(scope, binding, spec, cached, decodedBlobs.has(cached)); return; }
    if (!binding.visible || !cardRoute) return;
    if (!scope.queue.includes(binding)) {
      scope.queue.push(binding);
      scope.queue.sort((a, b) => a.index - b.index);
    }
    // A re-spec may already be in the queue: restore pending truth without
    // adding another entry or changing its position.
    setCardState(binding, "queued");
    pumpCards(scope);
  }
  function releaseCards(scope) {
    if (!scope) return;
    scope.observer?.disconnect();
    scope.firstPaintDone?.();
    scope.firstPaintDone = null;
    scope.queue.length = 0;
    for (const binding of scope.bindings) {
      releaseFlight(binding);
      if (binding.url && !binding.image.isConnected) { dropCardUrl(binding.url); binding.url = null; }
      binding.card?.removeAttribute("aria-busy");
      setCardMarker(binding, false);
    }
  }
  function refreshCards(formatKey) {
    const scope = cardScope;
    if (!scope?.data) return;
    for (const binding of scope.bindings) {
      if (formatKey && binding.formatKey !== formatKey) continue;
      const spec = cardSpec(scope.data, binding.formatKey);
      if (spec?.identity === binding.spec?.identity) continue;
      if (binding.flight && binding.flight !== cardFlights.get(spec?.identity)) releaseFlight(binding);
      binding.spec = spec;
      if (!spec || binding.shown !== spec.identity) {
        const pending = binding.image.dataset.swpCard;
        if (pending === "loading" || pending === "queued") setCardState(binding, binding.url ? "composed" : "photo");
        queueCard(scope, binding);
      }
    }
  }
  const closing = new WeakSet();
  // The scope whose cards are still on screen: a scope still waiting for its
  // catalogue hands over the cards it was about to release, and a closed
  // scope stays retired until the next render replaces its images.
  let retired = null;
  const onScreen = (scope) => (scope && !scope.data ? scope.previous : scope);
  function bindCards(cards) {
    const previous = cardScope ? onScreen(cardScope) : retired;
    retired = null;
    const scope = {bindings: [], queue: [], running: 0, observer: null, data: null, previous};
    cardScope = scope;
    const dialog = cards[0]?.image?.closest("dialog");
    if (dialog && !closing.has(dialog)) {
      closing.add(dialog);
      dialog.addEventListener("close", () => {
        const open = onScreen(cardScope);
        cardScope = null;
        retired = open;
        releaseCards(open);
      });
    }
    if (!cards.length || !cardRoute) { releaseCards(previous); return; }
    catalog().then((data) => {
      if (cardScope !== scope) return;
      scope.data = data;
      scope.bindings = cards.map((c, index) => ({
        index,
        image: c.image,
        card: c.image.closest("button"),
        formatKey: c.formatKey,
        label: c.label || c.image.alt,
        photo: {src: c.image.getAttribute("src"), srcset: c.image.getAttribute("srcset"), alt: c.image.alt},
        spec: cardSpec(data, c.formatKey),
        visible: false,
        shown: null,
        url: null,
        flight: null,
      }));
      // Claim in-flight identities the previous render still holds before
      // releasing it, so re-rendering the same cards never refetches them.
      for (const binding of scope.bindings) {
        const flight = binding.spec && cardFlights.get(binding.spec.identity);
        if (flight) { flight.users.add(binding); binding.flight = flight; }
      }
      releaseCards(previous);
      scope.previous = null;
      // Cards without a garment in this catalogue never compose; cards whose
      // artwork is still unchosen are watched so a later choice can apply.
      scope.bindings = scope.bindings.filter((b) => garmentFor(data, b.formatKey));
      if (!scope.bindings.length) return;
      for (const binding of scope.bindings) binding.image.dataset.swpCard = "photo";
      scope.observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          const binding = scope.bindings.find((b) => b.image === entry.target);
          if (!binding || binding.visible || !entry.isIntersecting) continue;
          binding.visible = true;
          scope.observer.unobserve(entry.target);
          queueCard(scope, binding);
        }
      });
      for (const binding of scope.bindings.slice(0, 3)) {
        const r = binding.image.getBoundingClientRect();
        let visible = r.width > 0 && r.height > 0 && r.top < innerHeight && r.bottom > 0 && r.left < innerWidth && r.right > 0;
        for (let p = binding.image.parentElement; visible && p && p !== document.body; p = p.parentElement) {
          if (!/(auto|scroll|hidden)/.test(getComputedStyle(p).overflowX + getComputedStyle(p).overflowY)) continue;
          const clip = p.getBoundingClientRect();
          visible = r.bottom > clip.top && r.top < clip.bottom && r.right > clip.left && r.left < clip.right;
        }
        if (visible) {
          binding.visible = true;
          binding.image.loading = "eager";
          queueCard(scope, binding);
        }
      }
      scope.firstPaint = new Promise((resolve) => { scope.firstPaintDone = resolve; });
      if (!scope.bindings.slice(0, 3).some((b) => b.visible && /^(queued|loading)$/.test(b.image.dataset.swpCard))) {
        scope.firstPaintDone();
        scope.firstPaintDone = null;
      }
      // Cached cards paint at once; every card is still watched so a later
      // colour or artwork choice only fetches once the card is visible.
      for (const binding of scope.bindings) {
        if (binding.spec && cardBlobs.has(binding.spec.identity)) queueCard(scope, binding);
        scope.observer.observe(binding.image);
      }
    }, () => releaseCards(previous));
  }
  document.addEventListener("swish:debut-cards", (event) => {
    const cards = (event.detail?.cards || []).filter((c) => c && c.image instanceof HTMLImageElement && c.formatKey);
    bindCards(cards);
  });
  // The studio may already have rendered its idle rail before this deferred
  // asset executes. Events are transient; claim its latest nodes once, then
  // follow subsequent picker/idle scope changes through the listener above.
  const initialCards = window.SwishFormatStudio?.previewCards?.();
  if (initialCards?.length) bindCards(initialCards);

  async function render(mount, formatKey) {
    ensureStyle();
    const ticket = {};
    mounts.set(mount, ticket);
    const state = {token: 0, colourId: null, final: false, note: "", eligible: false, staleDropped: 0, finalWidth: 0, painted: null};
    debug.mounts += 1;
    debug.last = state;
    let data;
    try { data = await catalog(); }
    catch {
      if (mounts.get(mount) !== ticket || !mount.isConnected || mount.closest("dialog")?.open === false) return;
      mount.replaceChildren(el("p", "swp__status", text("error", "Preview unavailable right now.")));
      state.note = "catalog-error";
      return;
    }
    if (mounts.get(mount) !== ticket || !mount.isConnected || mount.closest("dialog")?.open === false) return;
    if (data.cardPaths && cardScope?.firstPaint) {
      const scope = cardScope;
      await new Promise((resolve) => {
        const timer = setTimeout(resolve, 500);
        scope.firstPaint.then(() => { clearTimeout(timer); resolve(); });
      });
      if (mounts.get(mount) !== ticket || !mount.isConnected || mount.closest("dialog")?.open === false) return;
    }
    const explicit = data.artworkSelection === "explicit";
    state.artworkId = explicit ? (choices.get(previewChoiceKey(formatKey)) || null) : null;
    const designs = (data.designs || []).filter((d) => (d.productIds || []).includes(lookupId()));
    const garment = garmentFor(data, formatKey);
    if (!designs.length || !garment) { mount.replaceChildren(); state.note = "not-offered"; return; }
    const colours = garment.colours || data.colours || [];
    if (!colours.length) { mount.replaceChildren(); return; }
    // Paired catalogue: this product may carry paired original versions (for
    // example a dark-ink and a light-ink original), each exposed on its own
    // colours. A colour's design is the paired version the worker's catalogue
    // exposes for it; a colour no design covers stays a plain garment here.
    const designFor = (colour) =>
      designs.find((d) => (!explicit || d.id === state.artworkId) && !!colour.eligible && (d.eligibleVariants || []).includes(String(colour.id))) || null;
    const eligibleFor = (colour) => !!designFor(colour);
    const colourLabel = (colour) => colour.name + (explicit && !state.artworkId
      ? " · " + text("choose_artwork", "Choose artwork")
      : eligibleFor(colour) ? "" : " · " + text("unavailable_colour", "This artwork isn’t available on this colour."));
    const root = el("div", "swp");
    const eyebrow = el("p", "swp__eyebrow", text("eyebrow", "Live Debut preview"));
    const frame = el("div", "swp__frame");
    const canvas = el("canvas", "swp__canvas");
    canvas.width = 600; canvas.height = 600;
    canvas.setAttribute("role", "img");
    const final = el("img", "swp__final");
    final.alt = text("final_alt", "Finished Debut preview");
    final.hidden = true;
    frame.append(canvas, final);
    const row = el("div", "swp__row");
    const swatches = el("div", "swp__swatches");
    swatches.setAttribute("role", "group");
    swatches.setAttribute("aria-label", text("colours_label", "Colour"));
    const status = el("p", "swp__status");
    status.setAttribute("role", "status");
    const note = el("p", "swp__note", text("note", "Instant draft preview — the finished garment may vary slightly."));
    row.append(swatches, status);
    const artworks = el("div", "swp__artworks");
    if (explicit) {
      if (!designs.some((d) => d.id === state.artworkId)) state.artworkId = null;
      artworks.setAttribute("role", "group");
      artworks.setAttribute("aria-label", text("choose_artwork", "Choose artwork"));
      root.append(eyebrow, el("p", "swp__note", text("choose_artwork", "Choose artwork")), artworks,
        el("p", "swp__note", text("artwork_note", "Select artwork for this preview. It may differ from your product options.")), frame, row, note);
    } else root.append(eyebrow, frame, row, note);
    mount.replaceChildren(root);
    const ctx = canvas.getContext("2d");
    const finishedWidth = () => (matchMedia("(min-width:750px)").matches ? 1200 : 600);
    let shownUrl = null;
    let alive = true;
    const dialog = root.closest("dialog");
    const observer = new MutationObserver(() => {
      if (!root.isConnected || mounts.get(mount) !== ticket || dialog?.open === false) {
        alive = false; state.token += 1; observer.disconnect();
        if (shownUrl) URL.revokeObjectURL(shownUrl);
      }
    });
    observer.observe(document.body, {childList: true, subtree: true, attributes: true, attributeFilter: ["open"]});
    const current = (token) => alive && token === state.token && root.isConnected && mounts.get(mount) === ticket && dialog?.open !== false;
    async function select(colourId) {
      const token = ++state.token;
      state.colourId = colourId;
      // The format's card follows this colour and artwork choice.
      cardColours.set(formatKey, String(colourId));
      refreshCards(formatKey);
      const colour = colours.find((c) => String(c.id) === String(colourId));
      if (!colour) return;
      if (!explicit || !state.painted?.art) final.hidden = true;
      state.final = false;
      const paired = designFor(colour);
      const eligible = !!paired;
      state.eligible = eligible;
      for (const button of Array.from(swatches.children)) {
        button.setAttribute("aria-pressed", String(button.dataset.swpColour === String(colourId)));
        button.setAttribute("aria-label", colourLabel(colours.find((c) => String(c.id) === button.dataset.swpColour)));
      }
      // The new colour shows at once as the plain garment, so the previous
      // colour's artwork never stays on screen while this one composes.
      status.textContent = text("loading", "Composing preview…");
      if (!explicit || !state.painted?.art) {
        paint(ctx, null, "#ffffff", null, null);
        state.painted = {colourId: String(colourId), art: false};
      }
      let base, background = null;
      try {
        const layers = await Promise.all([
          bitmap(colour.blankPath || "/debut/preview/v1/asset/base"),
          colour.backgroundPath ? bitmap(colour.backgroundPath) : Promise.resolve(null),
        ]);
        if (!current(token)) return;
        [base, background] = layers;
        if (!explicit) paint(ctx, base, colour.hex, null, null, background);
      } catch {
        if (!current(token)) return;
        status.textContent = explicit && state.painted?.art ? text("kept_last", "Showing your last preview.") : text("error", "Preview unavailable right now.");
        state.note = "base-error";
        return;
      }
      // Immediate composition: colour underlay, photographic blank, then this
      // colour's paired artwork inside the worker's canonical placement.
      let art = null, placement = null, design = null;
      if (paired) {
        design = paired;
        status.textContent = text("loading", "Composing preview…");
        try {
          const [artBitmap, geometryBody] = await Promise.all([
            bitmap(productPrefix() + "/art/" + design.id),
            geometry(design.id, colourId),
          ]);
          if (!current(token)) return;
          art = artBitmap;
          // Gallery previews are strictly on demand. Reuse the selected bitmap;
          // opening, scrolling or focusing the gallery never fetches artwork.
          const thumb = artworks.querySelector('[data-swp-artwork="' + design.id + '"] canvas');
          if (thumb) {
            const tc = thumb.getContext("2d");
            tc.clearRect(0, 0, 66, 66);
            const scale = Math.min(66 / art.width, 66 / art.height);
            tc.drawImage(art, (66 - art.width * scale) / 2, (66 - art.height * scale) / 2, art.width * scale, art.height * scale);
          }
          placement = geometryBody;
        } catch {
          if (!current(token)) return;
          if (!explicit) paint(ctx, base, colour.hex, null, null, background);
          status.textContent = explicit && state.painted?.art ? text("kept_last", "Showing your last preview.") : text("error", "Preview unavailable right now.");
          state.note = "compose-error";
          return;
        }
      } else {
        status.textContent = "";
        state.note = "ineligible";
      }
      final.hidden = true;
      paint(ctx, base, colour.hex, art, placement, background);
      state.painted = {colourId: String(colourId), artworkId: design?.id || null, art: !!art};
      if (!paired) {
        status.textContent = explicit && !state.artworkId ? text("choose_artwork", "Choose artwork") : text("unavailable_colour", "This artwork isn’t available on this colour.");
        return;
      }
      // Deterministic finished image from the same canonical placement. A
      // stale fetch can never apply: only the current token swaps the image,
      // and on failure the canvas composition stays as the last good preview.
      const width = finishedWidth();
      fetch(CONFIG.origin + productPrefix() + "/render/" + design.id + "/" + colourId + "?width=" + width, {mode: "cors"})
        .then((r) => { if (!r.ok) throw new Error("render"); return r.blob(); })
        .then((blob) => new Promise((resolve, reject) => {
          // Decode off-DOM so an out-of-order response never touches the
          // visible image; only the current token swaps its source below.
          const url = URL.createObjectURL(blob);
          const probe = new Image();
          probe.onload = () => resolve(url);
          probe.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode")); };
          probe.src = url;
        }))
        .then((url) => {
          if (!current(token)) { URL.revokeObjectURL(url); state.staleDropped += 1; return; }
          if (shownUrl) URL.revokeObjectURL(shownUrl);
          shownUrl = url;
          final.src = url;
          final.hidden = false;
          state.final = true;
          state.finalWidth = width;
          state.note = "final";
          status.textContent = "";
        })
        .catch(() => {
          if (!current(token)) return;
          status.textContent = text("kept_last", "Showing your last preview.");
          state.note = "render-error-kept-canvas";
        });
    }
    if (explicit) designs.forEach((design, i) => {
      const button = el("button", "swp__artwork");
      button.type = "button";
      button.dataset.swpArtwork = design.id;
      button.setAttribute("aria-pressed", String(design.id === state.artworkId));
      const label = text("artwork", "Artwork") + " " + (i + 1);
      button.setAttribute("aria-label", label);
      const thumb = el("canvas");
      thumb.width = 66; thumb.height = 66;
      thumb.setAttribute("aria-hidden", "true");
      button.append(thumb, el("span", "", label));
      button.addEventListener("click", () => {
        state.artworkId = design.id;
        choices.set(previewChoiceKey(formatKey), design.id);
        for (const choice of artworks.children) choice.setAttribute("aria-pressed", String(choice === button));
        select(state.colourId);
      });
      artworks.append(button);
    });
    for (const colour of colours) {
      const button = el("button", "swp__swatch");
      button.type = "button";
      button.dataset.swpColour = String(colour.id);
      button.setAttribute("aria-label", colourLabel(colour));
      button.title = colour.name;
      const chip = el("span", "swp__chip");
      chip.setAttribute("aria-hidden", "true");
      chip.style.background = colour.hex;
      button.append(chip);
      button.addEventListener("click", () => select(colour.id));
      swatches.append(button);
    }
    // Initial preview: the lightest eligible garment — the live product page's
    // default white tee — independent of catalogue order; a fully ineligible
    // lineup falls back to the first colour's plain garment.
    const initial = colours.filter(eligibleFor).sort((a, b) => luma(b.hex) - luma(a.hex))[0] || colours[0];
    select(initial.id);
  }
  document.addEventListener("swish:debut-preview", (event) => {
    const detail = event.detail || {};
    if (detail.mount instanceof Element && detail.formatKey) render(detail.mount, detail.formatKey);
  });
})();
